import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type ReceiptPrint = Database['public']['Tables']['receipt_prints']['Row'];

export function useReceiptPrints(orderId: string | null) {
  const queryClient = useQueryClient();

  // Get print count for an order
  const { data: prints, isLoading } = useQuery({
    queryKey: ['receipt-prints', orderId],
    queryFn: async () => {
      if (!orderId) return [];
      
      const { data, error } = await supabase
        .from('receipt_prints')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as ReceiptPrint[];
    },
    enabled: !!orderId,
  });

  // Check if this would be a reprint (first print already done)
  const hasOriginalPrint = prints?.some(p => p.print_type === 'original') ?? false;
  const reprintCount = prints?.filter(p => p.print_type === 'reprint').length ?? 0;

  // Log a receipt print
  const logPrint = useMutation({
    mutationFn: async ({
      orderId,
      locationId,
      userId,
      printType,
      approvedById,
      printerName,
    }: {
      orderId: string;
      locationId: string;
      userId: string;
      printType: 'original' | 'reprint' | 'kitchen' | 'customer';
      approvedById?: string; // UUID of the supervisor who approved
      printerName?: string;
    }) => {
      const { data, error } = await supabase
        .from('receipt_prints')
        .insert({
          order_id: orderId,
          location_id: locationId,
          user_id: userId,
          print_type: printType,
          approved_by: approvedById,
          printer_name: printerName,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipt-prints', orderId] });
    },
  });

  return {
    prints: prints ?? [],
    isLoading,
    hasOriginalPrint,
    reprintCount,
    requiresApproval: hasOriginalPrint, // Reprints require manager approval
    logPrint,
  };
}

// Hook to verify manager PIN
export function useManagerPinVerification() {
  const verifyPin = useMutation({
    mutationFn: async ({ pin }: { pin: string }) => {
      // Get managers and admins with their PIN hashes
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the user's organization
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      // Get managers/admins in the organization with PINs set
      const { data: managers, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, pin_hash, role')
        .eq('organization_id', profile.organization_id)
        .in('role', ['manager', 'admin', 'owner', 'superadmin'])
        .eq('is_active', true)
        .not('pin_hash', 'is', null);

      if (error) throw error;

      // Use database function to verify PIN (bcrypt comparison)
      for (const manager of managers || []) {
        if (!manager.pin_hash) continue;
        
        const { data: isValid } = await supabase.rpc('verify_pin', {
          p_pin: pin,
          p_hash: manager.pin_hash,
        });

        if (isValid) {
          return {
            managerId: manager.id,
            managerName: `${manager.first_name || ''} ${manager.last_name || ''}`.trim(),
          };
        }
      }

      throw new Error('Invalid PIN');
    },
  });

  return {
    verifyPin,
    isVerifying: verifyPin.isPending,
  };
}





